import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  adminIntegrationsRepository,
  type IntegrationPendingResponse,
} from '@web/data/repositories';
import {
  AppIcons,
  Button,
  Card,
  EmptyHint,
  Input,
  useDebouncedValue,
  useOrgContext,
  useSnackbar,
} from '@web/ui/core';
import { t } from '@web/i18n';

import {
  ChatStatusChip,
  chipStateOfLink,
  companiesKey,
  formatDate,
  formatDateTime,
  integrationErrorMessage,
  integrationsKey,
  TOOL,
  useIntegrationsAccess,
} from './integrations_shared';

type PendingLink = IntegrationPendingResponse['links'][number];
type Unlinked = IntegrationPendingResponse['unlinked'][number];

function nextStepOf(link: PendingLink): string {
  if (link.status === 'review') return t('integrations.pending.next.review');
  if (link.expired) return t('integrations.pending.next.expired');
  return t('integrations.pending.next.pending');
}

function PendingRow({
  link,
  onOpen,
}: Readonly<{ link: PendingLink; onOpen: () => void }>): React.JSX.Element {
  return (
    <li>
      <button
        type="button"
        onClick={onOpen}
        className="flex w-full flex-col gap-1 border-t border-border-soft px-5 py-3 text-left first:border-t-0 hover:bg-muted"
      >
        <span className="flex items-center gap-2.5">
          <AppIcons name={TOOL[link.provider].icon} size={16} />
          <span className="truncate text-base font-medium text-foreground">
            {link.corporateGroupName}
          </span>
          <span className="ml-auto">
            <ChatStatusChip state={chipStateOfLink(link)} />
          </span>
        </span>
        <span className="text-meta text-muted-foreground">
          {t('integrations.pending.meta', {
            date: formatDateTime(link.createdAt),
            by: link.createdBy,
            expires: formatDate(link.expiresAt),
          })}
        </span>
        <span className="text-meta text-foreground">{nextStepOf(link)}</span>
      </button>
    </li>
  );
}

function CompanyPicker({ account }: Readonly<{ account: Unlinked }>): React.JSX.Element {
  const [q, setQ] = useState('');
  const term = useDebouncedValue(q.trim(), 300);
  const queryClient = useQueryClient();
  const { showSnackbar } = useSnackbar();
  const { orgId } = useOrgContext();
  const matches = useQuery({
    queryKey: companiesKey(term),
    queryFn: () => adminIntegrationsRepository.listCompanies(orgId, term),
    enabled: term.length >= 2,
  });
  const link = useMutation({
    mutationFn: (groupId: string) =>
      adminIntegrationsRepository.linkAccount(orgId, account.provider, account.account.id, groupId),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: integrationsKey });
      showSnackbar({ severity: 'success', message: t('integrations.toast.accountLinked') });
    },
    onError: (err) => {
      showSnackbar({ severity: 'error', message: integrationErrorMessage(err) });
    },
  });
  return (
    <div className="flex flex-col gap-2 px-5 pb-3">
      <Input
        type="search"
        value={q}
        onChange={(event) => {
          setQ(event.target.value);
        }}
        placeholder={t('integrations.search.placeholder')}
        aria-label={t('integrations.unlinked.searchLabel')}
      />
      <ul className="m-0 flex list-none flex-col gap-1 p-0">
        {(matches.data ?? []).map((company) => (
          <li key={company.id} className="flex items-center gap-2">
            <span className="truncate text-meta text-foreground">{company.name}</span>
            <span className="ml-auto">
              <Button
                size="sm"
                variant="outline"
                disabled={link.isPending}
                onClick={() => {
                  link.mutate(company.id);
                }}
              >
                {t('integrations.unlinked.linkTo', { group: company.name })}
              </Button>
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function UnlinkedRow({ account }: Readonly<{ account: Unlinked }>): React.JSX.Element {
  const [open, setOpen] = useState(false);
  const { canApprove } = useIntegrationsAccess();
  return (
    <li className="border-t border-border-soft first:border-t-0">
      <div className="flex items-center gap-2.5 px-5 py-3">
        <AppIcons name={TOOL[account.provider].icon} size={16} />
        <span className="flex min-w-0 flex-col">
          <span className="truncate text-base font-medium text-foreground">
            {account.account.name ?? account.account.id}
          </span>
          <span className="text-meta text-muted-foreground">
            {t('integrations.unlinked.meta', {
              id: account.account.id,
              date: formatDateTime(account.installedAt),
            })}
          </span>
        </span>
        <span className="ml-auto">
          {canApprove ? (
            <Button
              size="sm"
              variant={open ? 'ghost' : 'outline'}
              onClick={() => {
                setOpen(!open);
              }}
            >
              {open ? t('integrations.unlinked.close') : t('integrations.unlinked.link')}
            </Button>
          ) : (
            <span className="text-meta text-muted-foreground">
              {t('integrations.access.approveOnly')}
            </span>
          )}
        </span>
      </div>
      {open && canApprove ? <CompanyPicker account={account} /> : null}
    </li>
  );
}

export function PendingTab({
  query,
  onOpen,
}: Readonly<{
  query: ReturnType<typeof useQuery<IntegrationPendingResponse>>;
  onOpen: (groupId: string) => void;
}>): React.JSX.Element {
  if (query.isLoading) return <EmptyHint>{t('integrations.list.loading')}</EmptyHint>;
  if (query.isError) return <EmptyHint>{t('integrations.list.error')}</EmptyHint>;
  const { links, unlinked } = query.data ?? { links: [], unlinked: [] };
  if (links.length === 0 && unlinked.length === 0) {
    return <EmptyHint>{t('integrations.pending.empty')}</EmptyHint>;
  }
  return (
    <div className="flex flex-col gap-4">
      {links.length > 0 ? (
        <Card>
          <ul className="m-0 list-none p-0" aria-label={t('integrations.pending.label')}>
            {links.map((link) => (
              <PendingRow
                key={`${link.provider}-${link.corporateGroupId}-${link.createdAt}`}
                link={link}
                onOpen={() => {
                  onOpen(link.corporateGroupId);
                }}
              />
            ))}
          </ul>
        </Card>
      ) : null}
      {unlinked.length > 0 ? (
        <section className="flex flex-col gap-2">
          <h3 className="m-0 font-mono text-tiny font-medium uppercase tracking-eyebrow-wide text-muted-foreground">
            {t('integrations.unlinked.title')}
          </h3>
          <p className="m-0 text-meta text-muted-foreground">{t('integrations.unlinked.sub')}</p>
          <Card>
            <ul className="m-0 list-none p-0" aria-label={t('integrations.unlinked.title')}>
              {unlinked.map((account) => (
                <UnlinkedRow key={`${account.provider}-${account.account.id}`} account={account} />
              ))}
            </ul>
          </Card>
        </section>
      ) : null}
    </div>
  );
}
